import React, { useEffect, useMemo } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./BlogPostAscenseurConnecte.css";

export default function BlogPostAscenseurConnecte() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  // ✅ Robust slug matching (decode + match by key or slug)
  const post = useMemo(() => {
    const obj = t("blog.posts", { returnObjects: true }) || {};
    const cleanSlug = decodeURIComponent(String(slug || "")).trim();

    if (obj?.[cleanSlug]) return obj[cleanSlug];

    const list = Object.values(obj);
    return list.find((p) => String(p?.slug || "").trim() === cleanSlug) || null;
  }, [slug, t]);

  const notFound = !post;

  const content = post?.content || {};
  const sections = Array.isArray(content.sections) ? content.sections : [];

  const toc = useMemo(() => {
    if (!post) return [];
    const items = [];

    sections.forEach((s) => items.push({ id: s.id, title: s.title }));
    if (content?.iss?.title) items.push({ id: "iss", title: content.iss.title });
    if (content?.caseStudy?.title) items.push({ id: "case", title: content.caseStudy.title });
    if (Array.isArray(content?.takeaways) && content.takeaways.length) {
      items.push({ id: "takeaways", title: t("blog.key_takeaways") });
    }
    if (Array.isArray(content?.faq) && content.faq.length) {
      items.push({ id: "faq", title: content.faqTitle || t("blog.faq_title") });
    }
    if (content?.conclusionTitle) items.push({ id: "conclusion", title: content.conclusionTitle });

    return items;
  }, [post, sections, content, t]);

  // SEO + JSON-LD
  useEffect(() => {
    if (!post) {
      document.title = t("blog.post_not_found_title");
      return;
    }

    const metaTitle = post?.seo?.metaTitle || post.title;
    const metaDescription = post?.seo?.metaDescription || post.excerpt;

    document.title = metaTitle;

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", metaDescription);

    const canonicalHref = `${window.location.origin}/blog/${post.slug}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalHref);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Person", name: post.author },
      image: `${window.location.origin}${post.image}`,
      keywords: Array.isArray(post.tags) ? post.tags.join(", ") : "",
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalHref }
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      if (script && document.head.contains(script)) document.head.removeChild(script);
    };
  }, [post, t]);

  if (notFound) {
    return (
      <main className="post" aria-labelledby="post-title">
        <div className="post-outer">
          <NavLink to="/blog" className="post-back">
            {t("blog.back_to_blog")}
          </NavLink>
          <h1 id="post-title" className="post-title">
            {t("blog.post_not_found_title")}
          </h1>
          <p className="post-lede">{t("blog.post_not_found_text")}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="post" aria-labelledby="post-title">
      <div className="post-bg" aria-hidden="true" />

      <div className="post-outer">
        {/* Breadcrumb */}
        <nav className="post-crumbs" aria-label={t("blog.breadcrumb_aria")}>
          <NavLink to="/" className="crumb">
            {t("blog.breadcrumb_home")}
          </NavLink>
          <span aria-hidden="true">›</span>
          <NavLink to="/blog" className="crumb">
            {t("blog.breadcrumb_current")}
          </NavLink>
          <span aria-hidden="true">›</span>
          <span className="crumb crumb--current">{post.title}</span>
        </nav>

        {/* ✅ HERO (2 cards) */}
        <header className="post-hero">
          <section className="hero-card hero-card--text" aria-label="Article summary">
            <h1 id="post-title" className="post-title">
              {post.title}
            </h1>

            <p className="post-lede">{post.excerpt}</p>

            <div className="post-meta" aria-label="meta">
              <span className="chip">{post.category}</span>
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString(locale, {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
              <span aria-hidden="true">•</span>
              <span>
                {post.readTime} {t("blog.read_time_unit")}
              </span>
              <span aria-hidden="true">•</span>
              <span className="post-author">{post.author}</span>
            </div>

            <div className="post-tags" aria-label="tags">
              {(post.tags || []).map((tag) => (
                <NavLink key={tag} to={`/blog/tag/${encodeURIComponent(tag)}`} className="tag">
                  #{tag}
                </NavLink>
              ))}
            </div>

            <div className="hero-actions">
              <a className="hero-scroll" href="#toc">
                {t("blog.toc_title")} ↓
              </a>
              <NavLink className="hero-back" to="/blog">
                {t("blog.back_to_blog")}
              </NavLink>
            </div>
          </section>

          <figure className="hero-card hero-card--media">
            <img src={post.image} alt="" loading="eager" decoding="async" width="1200" height="675" />
          </figure>
        </header>

        {/* Layout: TOC + Content */}
        <div className="post-layout">
          <aside id="toc" className="post-toc" aria-label={t("blog.toc_title")}>
            <div className="post-toc-title">{t("blog.toc_title")}</div>
            <ul className="post-toc-list">
              {toc.map((it) => (
                <li key={it.id}>
                  <a href={`#${it.id}`} className="post-toc-link">
                    {it.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="post-content">
            {/* INTRO */}
            <section className="post-prose" aria-label="intro">
              {(content.introParagraphs || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </section>

            {/* SECTIONS */}
            <article className="post-prose" itemScope itemType="https://schema.org/BlogPosting">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="post-section" aria-label={s.title}>
                  <h2 className="post-h2">{s.title}</h2>

                  {(s.paragraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}

                  {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                    <ul className="post-ul">
                      {s.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}

                  {Array.isArray(s.steps) && s.steps.length > 0 && (
                    <ol className="post-ol">
                      {s.steps.map((st, i) => (
                        <li key={i}>{st}</li>
                      ))}
                    </ol>
                  )}
                </section>
              ))}

              {/* OPENlift */}
              {content?.iss?.title && (
                <section id="iss" className="post-section post-callout" aria-label={content.iss.title}>
                  <h2 className="post-h2">{content.iss.title}</h2>
                  {(content.iss.paragraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}

                  {Array.isArray(content.iss.points) && content.iss.points.length > 0 && (
                    <ul className="post-ul">
                      {content.iss.points.map((pt, i) => (
                        <li key={i}>{pt}</li>
                      ))}
                    </ul>
                  )}

                  <div className="post-cta-row">
                    <NavLink to={content.iss.ctaHref} className="post-cta">
                      {content.iss.ctaText}
                    </NavLink>
                  </div>
                </section>
              )}

              {/* CASE STUDY */}
              {content?.caseStudy?.title && (
                <section id="case" className="post-section" aria-label={content.caseStudy.title}>
                  <h2 className="post-h2">{content.caseStudy.title}</h2>
                  {(content.caseStudy.paragraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </section>
              )}

              {/* TAKEAWAYS */}
              {Array.isArray(content?.takeaways) && content.takeaways.length > 0 && (
                <section id="takeaways" className="post-section post-takeaways" aria-label={t("blog.key_takeaways")}>
                  <h2 className="post-h2">{t("blog.key_takeaways")}</h2>
                  <ul className="post-ul">
                    {content.takeaways.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </section>
              )}

              {/* FAQ */}
              {Array.isArray(content?.faq) && content.faq.length > 0 && (
                <section id="faq" className="post-section" aria-label={content.faqTitle || t("blog.faq_title")}>
                  <h2 className="post-h2">{content.faqTitle || t("blog.faq_title")}</h2>

                  <div className="post-faq">
                    {content.faq.map((item, i) => (
                      <details key={i} className="faq-item">
                        <summary className="faq-q">{item.q}</summary>
                        <div className="faq-a">
                          <p>{item.a}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {/* CONCLUSION */}
              {content?.conclusionTitle && (
                <section id="conclusion" className="post-section" aria-label={content.conclusionTitle}>
                  <h2 className="post-h2">{content.conclusionTitle}</h2>
                  {(content.conclusionParagraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </section>
              )}

              {/* FINAL CTA (global blog.cta_*) */}
              <section className="post-final-cta" aria-label={t("blog.cta_title")}>
                <div className="final-cta-card">
                  <div className="final-cta-text">
                    <h3 className="final-cta-title">{t("blog.cta_title")}</h3>
                    <p className="final-cta-desc">{t("blog.cta_text")}</p>
                  </div>
                  <NavLink to="/produits/passerelle-iss" className="final-cta-btn">
                    {t("blog.cta_button")}
                  </NavLink>
                </div>
              </section>

              <NavLink to="/blog" className="post-back post-back--bottom">
                {t("blog.back_to_blog")}
              </NavLink>
            </article>
          </div>
        </div>
      </div>
    </main>
  );
}
